import { AccessGuids } from 'src/app/core/consts';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { CompaniesService } from 'src/app/core/services/utelize/system/company/companies-service';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { CompanyLicenceSummaryDTO, CompanyLicenceSummaryDTO_EmptyObject } from 'src/app/core/models/system/widget/widget-company-licence-summary.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { Router } from '@angular/router';
import { WidgetCompanyAlertsBreakdownDTO, WidgetCompanyAlertsBreakdownDTO_EmptyObject } from 'src/app/core/models/system/widget/widget-company-alerts-breakdown.dto';
import { WidgetCompanyCostsBreakdownDTO } from 'src/app/core/models/system/widget/widget-company-costs-breakdown.dto';
import { WidgetLiveTimeLineDTO } from 'src/app/core/models/system/widget/widget-live-timeline.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

const ALERT_TITLE_BARRED_SIMS       ="Barred SIMs";
const ALERT_TITLE_PRODUCT_CHANGED   ="Product Changed";
const ALERT_TITLE_THROTTLED_SIMS    ="Throttled SIMs";
const ALERT_TITLE_USAGE             ="Usage Alerts";
const COST_TITLE_BUNDLE_COSTS       ="Bundle Costs";
const COST_TITLE_FEATURE_FEES       ="Feature Fees";
const COST_TITLE_OUT_OF_BUNDLE_USAGE="Out-of-Bundle Usage";
const COST_TITLE_PLATFORM_FEES      ="Platform Fees";
const LICENCE_COMPANIES_LIMIT       =7;
const RECENT_ACTIVITY_ROW_COUNT     =5;

export interface ActiveAlertItem {
  action           : string;
  actionActive     : string;
  alertType        : string;
  count            : number;
  iconBg           : string;
  iconCls          : string;
  severity         : string;
  subtitle         : string;
  thresholdBreached: string;
  title            : string;
}

export interface CostSummaryItem {
  iconBg  : string;
  iconCls : string;
  subtitle: string;
  title   : string;
  value   : string;
}

export interface LicenceCompany {
  name    : string;
  total   : number;
  used    : number;
}

export interface RecentActivityItem {
  iconBg : string;
  iconCls: string;
  text   : string;
  time   : string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-usage-dashboard-summary',
  templateUrl: './widget-row-usage-dashboard-summary.component.html',
  styleUrls: ['./widget-row-usage-dashboard-summary.component.scss']
})

export class WidgetRowUsageDashboardSummaryComponent implements OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() leadingTemplate    : TemplateRef<unknown> | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() useContainer       : boolean | undefined;

  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();

  private destroy$       =new Subject<void>();
  private filterCompanyID: number  | undefined;
  private filterDate     : OurDate | undefined;
  private refreshNonce   : number=0;
  private selection$     =new Subject<{ companyId: number; date: { year: number; month: number; day: number }; nonce: number }>();

  public activeAlerts!          : ActiveAlertItem[];
  public costSummaryItems!      : CostSummaryItem[];
  public expandContainer        : boolean=true;
  public licenceCompanies!      : LicenceCompany[];
  public licenceOverallAvailable: number=0;
  public licenceOverallTotal    : number=0;
  public licenceOverallUsed     : number=0;
  public licenceOverallPercent  : number=0;
  public licenceLoading         : boolean=true;
  public recentActivity!        : RecentActivityItem[];
  public visibleContainer       : boolean=true;

  get canViewTriggeredAlerts() { return this.componentAccessUserService.hasAccessGuid(AccessGuids.MenuItemSystemAdminTriggeredAlerts); }

  constructor(
    private authenticationService     : AuthenticationService,
    private changeDetectorRef         : ChangeDetectorRef,
    private companiesService          : CompaniesService,
    private componentAccessUserService: ComponentAccessUserService,
    private exceptionsService         : ExceptionsService,
    private router                    : Router,
    private widgetService             : WidgetService
  ) {

  }

  public alertClick(alert: ActiveAlertItem): void {
    try {
      if (!this.canViewTriggeredAlerts)
        return;

      if (alert == undefined || ((alert.alertType ?? "")=="" && (alert.action ?? "")==""))
        return;

      this.router.navigate(['/system-admin/triggered-alerts'], {
        queryParams: {
          action           : alert.action            || null,
          actionActive     : alert.actionActive      || null,
          alertType        : alert.alertType         || null,
          companyId        : this.filterCompanyID,
          thresholdBreached: alert.thresholdBreached || null
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnInit(): void {
    try {
      this.displayData();
      this.initSelectionStream();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Private Methods

  private applyCompanyAlertsBreakdown(data: WidgetCompanyAlertsBreakdownDTO): void {
    try {
      const countByTitle: { [title: string]: number }={
        //RBW_20260709 - Usage Alerts combines Spend Limit (type 1), % of Limit (type 2) and Data Usage (type 3).
        [ALERT_TITLE_USAGE          ]: this.toCount(data?.activeSpendLimitAlerts) +
                                       this.toCount(data?.activePercentageLimitAlerts) +
                                       this.toCount(data?.activeDataUsageAlerts),
        [ALERT_TITLE_BARRED_SIMS    ]: this.toCount(data?.activeAlertBars),
        [ALERT_TITLE_THROTTLED_SIMS ]: this.toCount(data?.activeAlertThrottles),
        [ALERT_TITLE_PRODUCT_CHANGED]: this.toCount(data?.activeAlertProductChanges)
      };

      this.activeAlerts?.forEach(alert => {
        if (countByTitle[alert.title]!==undefined)
          alert.count=countByTitle[alert.title];
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyCompanyCostsBreakdown(data: WidgetCompanyCostsBreakdownDTO[], company: CompanyDTO | undefined): void {
    try {
      const sorted=this.sortByPreferredCurrency(data ?? [], company);

      const subtitleByTitle: { [title: string]: string }={
        [COST_TITLE_BUNDLE_COSTS       ]: this.formatMultiCurrency(sorted, e => e?.bundleCosts),
        [COST_TITLE_PLATFORM_FEES      ]: this.formatMultiCurrency(sorted, e => e?.platformFees),
        [COST_TITLE_OUT_OF_BUNDLE_USAGE]: this.formatMultiCurrency(sorted, e => e?.outOfBundleUsage),
        [COST_TITLE_FEATURE_FEES       ]: this.formatMultiCurrency(sorted, e => e?.featureFees)
      };

      this.costSummaryItems?.forEach(item => {
        if (subtitleByTitle[item.title]!==undefined)
          item.subtitle=subtitleByTitle[item.title];
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private sortByPreferredCurrency(list: WidgetCompanyCostsBreakdownDTO[], company: CompanyDTO | undefined): WidgetCompanyCostsBreakdownDTO[] {
    try {
      const companySymbol  =(company?.invoiceCurrencySymbol ?? "").trim();
      const preferenceOrder=[companySymbol, "£", "$", "€"]
                              .filter(s => s.length>0)
                              .filter((s, idx, arr) => arr.indexOf(s)===idx);

      return [...list].sort((a, b) => {
        const aIdx=preferenceOrder.indexOf((a?.currencySymbol ?? "").trim());
        const bIdx=preferenceOrder.indexOf((b?.currencySymbol ?? "").trim());

        if (aIdx===-1 && bIdx===-1) return 0;
        if (aIdx===-1) return  1;
        if (bIdx===-1) return -1;
        return aIdx - bIdx;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return list;
    }
  }

  private formatMultiCurrency(list: WidgetCompanyCostsBreakdownDTO[], extract: (entry: WidgetCompanyCostsBreakdownDTO) => number | null | undefined): string {
    try {
      const parts=list
        .map(entry => this.formatCurrency(extract(entry), entry?.currencySymbol ?? ""))
        .filter(s => s!=="—");

      return parts.length>0 ? parts.join(" + ") : "—";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "—";
    }
  }

  private applyCompanyTimeline(data: WidgetLiveTimeLineDTO[]): void {
    try {
      this.recentActivity=(data ?? []).map(dto => this.mapTimelineToActivity(dto));

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyCompanyLicenceSummary(data: CompanyLicenceSummaryDTO): void {
    try {
      const summary  =data ?? CompanyLicenceSummaryDTO_EmptyObject();
      const used     =summary.usedLicences      ?? 0;
      const available=summary.availableLicences ?? 0;
      const total    =summary.totalLicences     ?? 0;

      this.licenceOverallUsed     =used;
      this.licenceOverallAvailable=available;
      this.licenceOverallTotal    =total;
      this.licenceOverallPercent  =total>0 ? Math.round((used / total) * 1000) / 10 : 0;
      this.licenceLoading         =false;

      this.licenceCompanies=(summary.companies ?? []).slice(0, LICENCE_COMPANIES_LIMIT).map(c => ({
        total    : c.totalLicences ?? 0,
        name     : c.companyName   ?? "",
        used     : c.usedLicences  ?? 0
      }));

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private displayData(): void {
    try {
      this.recentActivity=[];

      this.costSummaryItems=[
        { iconBg: "bg-primary-subtle text-primary", iconCls: "bi bi-box-seam",      subtitle: "—", title: COST_TITLE_BUNDLE_COSTS,        value: "" },
        { iconBg: "bg-info-subtle text-info",       iconCls: "bi bi-server",        subtitle: "—", title: COST_TITLE_PLATFORM_FEES,       value: "" },
        { iconBg: "bg-danger-subtle text-danger",   iconCls: "bi bi-cash-coin",     subtitle: "—", title: COST_TITLE_OUT_OF_BUNDLE_USAGE, value: "" },
        { iconBg: "bg-warning-subtle text-warning", iconCls: "bi bi-stars",         subtitle: "—", title: COST_TITLE_FEATURE_FEES,        value: "" }
      ];

      this.licenceCompanies=[
        { name: "Manufacturing Co.", total: 500, used: 485 },
        { name: "Logistics Ltd.",    total: 400, used: 392 },
        { name: "Retail Group",      total: 300, used: 248 }
      ];

      this.activeAlerts=[
        { alertType: "usage", action: "",              actionActive: "all", thresholdBreached: "yes", count: 0, iconBg: "bg-warning-subtle text-warning", iconCls: "bi bi-exclamation-triangle",   severity: "High",   subtitle: "", title: ALERT_TITLE_USAGE           },
        { alertType: "",      action: "barred",        actionActive: "yes", thresholdBreached: "all", count: 0, iconBg: "bg-danger-subtle text-danger",   iconCls: "bi bi-slash-circle",           severity: "Medium", subtitle: "", title: ALERT_TITLE_BARRED_SIMS     },
        { alertType: "",      action: "throttled",     actionActive: "yes", thresholdBreached: "all", count: 0, iconBg: "bg-info-subtle text-info",       iconCls: "bi bi-speedometer",            severity: "Info",   subtitle: "", title: ALERT_TITLE_THROTTLED_SIMS  },
        { alertType: "",      action: "productchanged",actionActive: "yes", thresholdBreached: "all", count: 0, iconBg: "bg-primary-subtle text-primary", iconCls: "bi bi-arrow-up-right-square",  severity: "Info",   subtitle: "", title: ALERT_TITLE_PRODUCT_CHANGED }
      ];

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day   &&
          a.nonce     ===b.nonce
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;

          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );

          this.licenceLoading=true;
          this.changeDetectorRef.markForCheck();

          const userId=-1;//this.authenticationService.currentUserObject()?.userId ?? 0;

          return combineLatest({
            company               : safeStart(this.companiesService.getById                 (companyId),                                  <CompanyDTO | undefined>undefined),
            companyAlertsBreakdown: safeStart(this.widgetService.getCompanyAlertsBreakdown(companyId, year, month, day),                 WidgetCompanyAlertsBreakdownDTO_EmptyObject()),
            companyCostsBreakdown : safeStart(this.widgetService.getCompanyCostsBreakdown (userId, companyId, year, month, day),         <WidgetCompanyCostsBreakdownDTO[]>[]),
            companyLicenceSummary : safeStart(this.widgetService.getCompanyLicenceSummary (companyId, year, month, day),                 CompanyLicenceSummaryDTO_EmptyObject()),
            companyTimeline       : safeStart(this.widgetService.getCompanyTimeline       (userId, companyId, RECENT_ACTIVITY_ROW_COUNT), <WidgetLiveTimeLineDTO[]>[])
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyCompanyAlertsBreakdown(result.companyAlertsBreakdown);
        this.applyCompanyCostsBreakdown (result.companyCostsBreakdown, result.company);
        this.applyCompanyLicenceSummary (result.companyLicenceSummary);
        this.applyCompanyTimeline       (result.companyTimeline);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private mapTimelineToActivity(dto: WidgetLiveTimeLineDTO): RecentActivityItem {
    try {
      const baseMessage =dto?.message      ?? "";
      const affectedUser=(dto?.affectedUser ?? "").trim();
      const message     =affectedUser ? baseMessage+" for "+affectedUser : baseMessage;
      const lower       =message.toLowerCase();

      let iconBg : string="bg-info-subtle text-info";
      let iconCls: string="bi bi-clock-history";

      if (lower.includes("provisioned") || lower.includes("activated") || lower.includes("created")) {
        iconBg ="bg-success-subtle text-success";
        iconCls="bi bi-check-circle";
      }
      else if (lower.includes("barred") || lower.includes("blocked") || lower.includes("suspended")) {
        iconBg ="bg-danger-subtle text-danger";
        iconCls="bi bi-slash-circle";
      }
      else if (lower.includes("limit") || lower.includes("threshold")) {
        iconBg ="bg-success-subtle text-success";
        iconCls="bi bi-graph-up-arrow";
      }
      else if (lower.includes("zone") || lower.includes("country") || lower.includes("location")) {
        iconBg ="bg-warning-subtle text-warning";
        iconCls="bi bi-geo-alt";
      }
      else if (lower.includes("sim") || lower.includes("switched")) {
        iconBg ="bg-primary-subtle text-primary";
        iconCls="bi bi-sim";
      }

      return {
        iconBg : iconBg,
        iconCls: iconCls,
        text   : message,
        time   : this.formatRelativeTime(dto?.enteredDate)
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { iconBg: "bg-info-subtle text-info", iconCls: "bi bi-clock-history", text: "", time: "" };
    }
  }

  private toCount(value: number | null | undefined): number {
    try {
      if (value===null || value===undefined || isNaN(value))
        return 0;

      return Math.max(0, Math.round(value));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  private formatCurrency(value: number | null | undefined, symbol: string=""): string {
    try {
      if (value===null || value===undefined || isNaN(value))
        return "—";

      const prefix=symbol?.trim().length>0 ? symbol : "£";

      return prefix+value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "—";
    }
  }

  private formatRelativeTime(date: Date | string | null | undefined): string {
    try {
      if (!date)
        return "";

      const d     =new Date(date);
      const diffMs=Date.now() - d.getTime();

      if (isNaN(diffMs) || diffMs<0)
        return "";

      const diffMins=Math.floor(diffMs / 60000);
      const diffHrs =Math.floor(diffMins / 60);
      const diffDays=Math.floor(diffHrs / 24);

      if (diffDays>=1)
        return diffDays+" day"+(diffDays>1 ? "s" : "")+" ago";

      if (diffHrs>=1)
        return diffHrs+" hour"+(diffHrs>1 ? "s" : "")+" ago";

      if (diffMins>=1)
        return diffMins+" min"+(diffMins>1 ? "s" : "")+" ago";

      return "just now";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private triggerFetch(): void {
    try {
      if (this.filterCompanyID===undefined || this.filterCompanyID===null)
        return;

      const date={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1)
      };

      this.selection$.next({ companyId: this.filterCompanyID, date, nonce: this.refreshNonce });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public Methods

  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate ?? populateOurDate(this.exceptionsService, Date.now());

      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refresh(): void {
    try {
      this.refreshNonce++;
      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
